/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import {GenericObject} from '../types/entities';
import {DateTime} from '../types/general';
import {DefaultDateFormats, isSame, toDateISOString, toISOString} from './DateUtil';
import {stringToNumber} from './StringUtil';

// defines the prop definitions for alphaSort util
export type SortProps = {
  items: string[] | GenericObject[];
  order?: 'asc' | 'desc';
  caseSensitive?: boolean;
};

/**
 * Provides an alphabetical sort on a list of items of string/object.
 */
export const alphaSort = ({items, order = 'asc', caseSensitive = false}: SortProps) => {
  return items.slice().sort((x, y) => {
    const a      = (!isString(x) ? x.name || Object.keys(x)[0] : x);
    const b      = (!isString(y) ? y.name || Object.keys(y)[0] : y);
    const valueA = caseSensitive ? a : a.toLowerCase();
    const valueB = caseSensitive ? b : b.toLowerCase();
    const type   = order === 'asc' ? valueA > valueB : valueB > valueA;
    return valueA === valueB ? 0 : type ? 1 : -1;
  });
};

/**
 * Provides an alphabetical sort on a list of items of object.
 */
export const alphaSortForObject = (items: object, order = 'asc', property = 'title') => {
  return Object.entries(items).sort(([, x], [, y]) => {
    const valueA = x[property] || '';
    const valueB = y[property] || '';

    const a = isString(valueA) ? valueA.toLowerCase() : valueA;
    const b = isString(valueB) ? valueB.toLowerCase() : valueB;
    const type = order === 'asc' ? a > b : b > a;
    return a === b ? 0 : type ? 1 : -1;
  });
};

/**
 * Transforms the string versions of the date and sorts them by date rather than
 * alphabetically.
 */
export const dateSortComparator = (valueA: string, valueB: string): number => {
  const dateA = toISOString(valueA).substr(0, 10);
  const dateB = toISOString(valueB).substr(0, 10);
  if (dateA !== dateB) {
    return (dateA > dateB) ? 1 : -1;
  }
  return 0;
};

/**
 * Transforms the string versions of the date/time and sorts them by date/time
 * rather than alphabetically.
 */
export const dateTimeSortComparator = (valueA: string, valueB: string, inputFormat?: string): number => {
  const dateFormat = isString(inputFormat) ? inputFormat : null;
  const dateTimeA  = toISOString(valueA, 'current', dateFormat);
  const dateTimeB  = toISOString(valueB, 'current', dateFormat);
  if (dateTimeA !== dateTimeB) {
    return (dateTimeA > dateTimeB) ? 1 : -1;
  }
  return 0;
};

/**
 * Provides a custom rule for sorting dates that are strings and need to be
 * transformed to Dates before being compared.
 */
export const dateFilterComparator = (valueA: string, valueB: string): number => {
  const dateA = toISOString(valueA)?.substr(0, 10);
  const dateB = toISOString(valueB)?.substr(0, 10);
  if (dateA !== dateB) {
    return (dateA > dateB) ? -1 : 1;
  }
  return 0;
};

/**
 * Provides a custom rule for sorting dates that are strings and need to be
 * transformed to Dates before being compared for Grid filters.
 *
 * ```typescript
 *
 *  const result1 = GridFilterDateComparator('12/01/2023', '13/01/2023', 'DD/MM/YYYY'); // 1
 *  const result2 = GridFilterDateComparator('12/01/2023', '12/01/2023', 'DD/MM/YYYY'); // 0
 *  const result3 = GridFilterDateComparator('01/13/2023', '01/12/2023', 'MM/DD/YYYY'); // -1
 *
 * ```
 */
export const clientGridFilterDateComparator = (valueA: Date, valueB: string, inputFormat?: string): number => {
  const dateParts = valueB.split('/');
  let day    = parseInt(dateParts[0], 10);
  let month  = parseInt(dateParts[1], 10) - 1;
  const year = parseInt(dateParts[2], 10);
  if (inputFormat === DefaultDateFormats.US) {
    day   = parseInt(dateParts[1], 10);
    month = parseInt(dateParts[0], 10) - 1;
  }
  const date = new Date(year, month, day);
  if (date.getTime() !== valueA.getTime()) {
    return (date.getTime() < valueA.getTime()) ? -1 : 1;
  }
  return 0;
};

/**
 * Provides a custom rule for sorting dates/times that are strings and need to be
 * transformed to Dates before being compared.
 */
export const dateTimeFilterComparator = (
  valueA: DateTime, valueB: DateTime, comparator: Function = toISOString): number => {
  const dateTimeA = comparator(valueA);
  const dateTimeB = comparator(valueB);
  if (dateTimeA !== dateTimeB) {
    return (dateTimeA > dateTimeB) ? -1 : 1;
  }
  return 0;
};

/**
 * Provides a custom rule for sorting dates that are strings and need to be
 * transformed to Dates before being compared. This function is used where the
 * client side filter has to be applied and dates does not need to be converted
 * to utc format.
 *
 * ```typescript
 *
 *  import {dateTimeISOFilterComparator} from '@ion/core/src/utils/';
 *
 *  const valueA = new Date('Fri Mar 25 2022 00:00:00 GMT+0530 (India Standard Time)');
 *  const valueB = new Date(1558081858000); // Friday, May 17, 2019 2:00:58 PM GMT+05:30
 *  const valueC = new Date(1663933927000); // Friday, September 23, 2022 5:22:07 PM GMT+05:30
 *  const valueD = new Date(1648216867000); // Friday, March 25, 2022 7:31:07 PM GMT+05:30
 *
 *  const actual = dateTimeISOFilterComparator(valueA, valueB); // output: -1
 *  const actual = dateTimeISOFilterComparator(valueA, valueC); // output:  1
 *  const actual = dateTimeISOFilterComparator(valueA, valueD); // output:  0
 *
 * ```
 */
export const dateTimeISOFilterComparator = (valueA: DateTime, valueB: DateTime) =>
  dateTimeFilterComparator(valueA, valueB, toDateISOString);

/**
 * Values can be returned to a sort function to sort strings alphabetically.
 */
export const textSortComparator = (valueA: string, valueB: string, caseSensitive = true): number => {
  const valA = caseSensitive ? valueA : valueA?.toLowerCase();
  const valB = caseSensitive ? valueB : valueB?.toLowerCase();
  if (valA !== valB) {
    return (valA > valB) ? 1 : -1;
  }
  return 0;
};

/**
 * Provides a custom rule for sorting to values that can be returned to a sort function to sort strings alphabetically.
 * This function is used where the client side filter has to be applied and text need to check with in case sensitive.
 * 
 * ```typescript
 * 
 *  import {textInCaseSensitiveSortComparator} from '@ion/core/src/utils';
 *
 *  const actual = textInCaseSensitiveSortComparator(valueA, valueB); // output: -1
 *  const actual = textInCaseSensitiveSortComparator(valueA, valueC); // output:  1 
 *  const actual = textInCaseSensitiveSortComparator(valueA, valueD); // output:  0
 * 
 * ```
 */
export const textInCaseSensitiveSortComparator = (valueA: string, valueB: string) =>
  textSortComparator(valueA, valueB, false)
;

/**
 * Transforms the string versions of the number and sorts them by number rather than
 * alphabetically.
 */
export const numberSortComparator = (valueA: string, valueB: string): number => {
  const numberA = stringToNumber(valueA);
  const numberB = stringToNumber(valueB);
  if (numberA !== numberB) {
    return (numberA > numberB) ? 1 : -1;
  }
  return 0;
};

/**
 * Custom rule for sorting numbers that are strings and need to be transformed to
 * numbers before being compared.
 */
export const numberFilterComparator = (valueA: string, valueB: string): number => {
  const numberA = stringToNumber(valueA);
  const numberB = stringToNumber(valueB);
  if (numberA !== numberB) {
    return (numberA > numberB) ? -1 : 1;
  }
  return 0;
};

/**
 * The defalutSortComparator will be useful to disable the client-side sorting for defined
 * columns. The reason is we may need to disable the client-side sorting when we are using
 * React-Query with grids. It gives the sorted data to the client-side grid and the grid also
 * try to sort the data, since the model is a client model. To avoid this problem, we return
 * zero always to the comparator property of the specified column.
 *
 * @returns zero always to show the server data for client model grids.
 *
 * ```typescript
 *
 *   import {defalutSortComparator} from '@ion/core/src/utils/';
 *
 *   ...
 *   }, {
 *     headerName   : header,
 *     ...,
 *     comparator   : defalutSortComparator,
 *     filterParams : defaultEqualsFilterParams
 *   }, {
 *   ...
 *
 * ```
 *
 */
export const defalutSortComparator = () => 0;

/**
 * Recursive function that sorts a deeply nested account hierarchy by the passed
 * AccountLink property. Strict mode would place sorted uppercase strings before
 * sorted lowercase strings.
 */
export const sortNestedAccounts = ({accounts, property = 'title', order = 'asc', strict = false}) => {
  accounts.sort((a, b) => {
    const sanitizedA: any = (a.subaccount || a)[property];
    const sanitizedB: any = (b.subaccount || b)[property];
    const cleanedA        = (strict || !isString(sanitizedA)) ? sanitizedA : sanitizedA.toLowerCase();
    const cleanedB        = (strict || !isString(sanitizedB)) ? sanitizedB : sanitizedB.toLowerCase();
    const type            = order === 'asc' ? cleanedA > cleanedB : cleanedB > cleanedA;
    return a === b ? 0 : type ? 1 : -1;
  });
  accounts.forEach(item => {
    if (item.subAccounts || item.subaccount?.subAccounts) {
      sortNestedAccounts({accounts: item.subaccount?.subAccounts || item.subAccounts, order});
    }
  });
  return accounts;
};

/**
 * The Function that sorts the provided array based on two properties in specified order.
 * If values of property1 are identical, compares the values of property2.
 */
export const multiLevelSort = ({items, property1, property2, order = 'asc'}) => {
  if (items?.length) {
    return items.sort((itemA, itemB) => {
      const a1 = itemA[property1];
      const b1 = itemB[property1];
      if (isSame(a1, b1, 'second')) {
        const a2 = itemA[property2];
        const b2 = itemB[property2];
        return order === 'asc' ? a2 - b2 : b2 - a2;
      }
    });
  }
  return [];
};
